type PictureProperty = {
  key?: unknown
  values?: unknown
}

type PictureCarrier = {
  pictures?: unknown
  properties?: PictureProperty[]
}

export type PicturesReadSource = 'auto' | 'top-level' | 'properties'
export type PicturesWriteStorage = 'top-level' | 'properties'

export const normalizePictures = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    const isAlreadyValid = value.every(
      (entry) => typeof entry === 'string' && entry.length > 0,
    )
    if (isAlreadyValid) {
      return value as string[]
    }

    return value.filter(
      (entry): entry is string => typeof entry === 'string' && entry.length > 0,
    )
  }

  if (typeof value === 'string' && value.length > 0) {
    return [value]
  }

  return []
}

export const readPicturesProperty = (
  row: PictureCarrier | null | undefined,
): string[] => {
  if (!Array.isArray(row?.properties)) return []

  const picturesProperty = row.properties.find(
    (property) => property?.key === 'pictures',
  )

  return normalizePictures(picturesProperty?.values)
}

export const readPicturesFromRow = (
  row: PictureCarrier | null | undefined,
  options?: {
    source?: PicturesReadSource
  },
): string[] => {
  const source = options?.source ?? 'auto'

  if (source === 'properties') {
    return readPicturesProperty(row)
  }

  if (source === 'top-level') {
    return normalizePictures(row?.pictures)
  }

  const picturesFromProperties = readPicturesProperty(row)
  if (picturesFromProperties.length > 0) {
    return picturesFromProperties
  }

  return normalizePictures(row?.pictures)
}

export const writePicturesToRow = <Row extends PictureCarrier>(
  row: Row,
  pictures: string[],
  options?: {
    storage?: PicturesWriteStorage
    mirrorTopLevel?: boolean
  },
): Row => {
  const storage = options?.storage ?? 'top-level'
  const mirrorTopLevel = options?.mirrorTopLevel ?? true
  const normalizedPictures = normalizePictures(pictures)
  const nextRow = { ...row } as Row & PictureCarrier

  if (storage === 'properties') {
    const nextProperties = Array.isArray(row.properties)
      ? [...row.properties]
      : []
    const picturesPropertyIndex = nextProperties.findIndex(
      (property) => property?.key === 'pictures',
    )
    const nextPicturesProperty = {
      key: 'pictures',
      values: normalizedPictures,
    }

    if (picturesPropertyIndex === -1) {
      nextProperties.push(nextPicturesProperty)
    } else {
      nextProperties[picturesPropertyIndex] = {
        ...nextProperties[picturesPropertyIndex],
        ...nextPicturesProperty,
      }
    }

    nextRow.properties = nextProperties as Row['properties']

    if (mirrorTopLevel) {
      nextRow.pictures = normalizedPictures
    }

    return nextRow as Row
  }

  nextRow.pictures = normalizedPictures
  return nextRow as Row
}
